import { useEffect, useState } from 'react';
import type { Tables } from '@auctisync/shared';
import { supabase } from '../lib/supabase';

type PreviewEvent = Pick<
  Tables<'auction_preview_events'>,
  'id' | 'title' | 'location' | 'starts_at' | 'ends_at' | 'notes'
>;

interface AuctionPreviewEventsProps {
  auctionId: string;
}

/**
 * Module #23 chunk 7 — public list of scheduled preview events for an auction.
 *
 * Renders nothing when there are no events or the fetch fails — the component
 * is purely additive. Future events first; past events hidden (the auction
 * detail page already covers "what was here historically" implicitly).
 */
export function AuctionPreviewEvents({ auctionId }: AuctionPreviewEventsProps) {
  const [events, setEvents] = useState<PreviewEvent[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('auction_preview_events')
        .select('id, title, location, starts_at, ends_at, notes')
        .eq('auction_id', auctionId)
        .gte('starts_at', new Date(Date.now() - 60 * 60 * 1000).toISOString()) // show events from last hour onward
        .order('starts_at', { ascending: true });
      if (cancelled) return;
      if (error) {
        setEvents([]);
        return;
      }
      setEvents(data as PreviewEvent[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [auctionId]);

  if (!events || events.length === 0) return null;

  return (
    <section
      style={{
        marginTop: '1.5rem',
        padding: '1rem 1.25rem',
        border: '1px solid var(--border)',
        borderRadius: '0.5rem',
        background: 'var(--surface-muted)',
      }}
    >
      <h2
        style={{
          fontSize: '0.85rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-muted)',
          marginBottom: '0.75rem',
        }}
      >
        Preview events
      </h2>
      <ul style={{ display: 'grid', gap: '0.75rem', listStyle: 'none', padding: 0, margin: 0 }}>
        {events.map((e) => (
          <li
            key={e.id}
            style={{
              borderLeft: '3px solid var(--brand-accent)',
              paddingLeft: '0.75rem',
            }}
          >
            <div style={{ fontWeight: 600, color: 'var(--text)' }}>{e.title}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              {formatEventTime(e.starts_at, e.ends_at)}
            </div>
            {e.location ? (
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>📍 {e.location}</div>
            ) : null}
            {e.notes ? (
              <div style={{ fontSize: '0.85rem', color: 'var(--text)', marginTop: '0.25rem' }}>
                {e.notes}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function formatEventTime(startsAt: string, endsAt: string | null): string {
  const start = new Date(startsAt);
  const startStr = start.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  if (!endsAt) return startStr;
  const end = new Date(endsAt);
  const sameDay = start.toDateString() === end.toDateString();
  const endStr = sameDay
    ? end.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' })
    : end.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
  return `${startStr} → ${endStr}`;
}
